/**
 * FloatingVideoPlayer
 * ──────────────────────────────────────────────────────────────────────────
 * Remplace le bloc embeddedMedia actuel dans ScenarioPlayer.
 *
 * Comportement :
 *   Desktop (>= 1024px) : overlay flottant fixé en bas-droite, redimensionnable,
 *                         draggable, sans pousser le contenu.
 *   Mobile  (<  1024px) : drawer depuis le bas, 75vh, swipe-to-close.
 *
 * Props :
 *   media    : { type: 'video'|'youtube'|'pdf', src: string, title: string }
 *   onClose  : () => void
 *
 * Détection YouTube : si src contient "youtube.com/embed" ou "youtu.be"
 *   → rendu <iframe> avec thumbnail lazy + play-on-click
 *   → sinon <video> natif
 *
 * Usage dans ScenarioPlayer — remplacer le bloc AnimatePresence embeddedMedia :
 *
 *   <AnimatePresence>
 *     {embeddedMedia && (
 *       <FloatingVideoPlayer
 *         media={embeddedMedia}
 *         onClose={clearEmbeddedMedia}
 *       />
 *     )}
 *   </AnimatePresence>
 *
 * Dépendances : framer-motion (déjà présent), aucune autre.
 */

import { motion, useDragControls } from 'framer-motion';
import { GripHorizontal, Maximize2, Minimize2, Play, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// ─── Utilitaires YouTube ───────────────────────────────────────────────────

function isYoutube(src) {
  return src && (src.includes('youtube.com') || src.includes('youtu.be'));
}

/**
 * Extrait l'ID YouTube depuis une URL embed ou standard.
 * Gère les cas :
 *   https://www.youtube.com/embed/VIDEO_ID
 *   https://www.youtube.com/embed/VIDEO_ID?start=30
 *   https://youtu.be/VIDEO_ID
 *   https://www.youtube.com/watch?v=VIDEO_ID
 */
function extractYoutubeId(src) {
  if (!src) return null;
  const embedMatch  = src.match(/youtube\.com\/embed\/([^?&\s]+)/);
  if (embedMatch) return embedMatch[1];
  const shortMatch  = src.match(/youtu\.be\/([^?&\s]+)/);
  if (shortMatch) return shortMatch[1];
  const watchMatch  = src.match(/[?&]v=([^&\s]+)/);
  if (watchMatch) return watchMatch[1];
  return null;
}

function youtubeThumbnail(id) {
  // maxresdefault souvent disponible, fallback hqdefault
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

// ─── Constantes layout ─────────────────────────────────────────────────────

const DESKTOP_W  = 480;
const DESKTOP_H  = 270;  // 16:9
const DESKTOP_W_MAX = 760;
const DESKTOP_H_MAX = 428;

// ─── YoutubeLazyEmbed ──────────────────────────────────────────────────────

function YoutubeLazyEmbed({ src, title, autoplay = false }) {
  const [activated, setActivated] = useState(autoplay);
  const ytId = extractYoutubeId(src);
  const thumb = ytId ? youtubeThumbnail(ytId) : null;

  // Construire l'URL embed avec autoplay
  const embedSrc = src
    ? (src.includes('?')
        ? `${src}&autoplay=1&rel=0`
        : `${src}?autoplay=1&rel=0`)
    : src;

  if (!activated) {
    return (
      <button
        onClick={() => setActivated(true)}
        className="relative w-full h-full bg-black flex items-center justify-center group focus:outline-none"
        aria-label={`Lire la vidéo : ${title}`}
      >
        {thumb && (
          <img
            src={thumb}
            alt={title || 'Aperçu vidéo'}
            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity duration-300"
            loading="lazy"
          />
        )}
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        {/* Bouton play */}
        <div className="relative z-10 w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:scale-110 group-hover:bg-white/20 transition-all duration-200 shadow-2xl">
          <Play size={28} className="text-white ml-1" fill="white" />
        </div>
        {/* Titre en bas */}
        {title && (
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 z-10">
            <p className="text-xs text-white/70 font-medium truncate">{title}</p>
          </div>
        )}
      </button>
    );
  }

  return (
    <iframe
      src={embedSrc}
      title={title || 'Vidéo'}
      className="w-full h-full border-none"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
}

// ─── FloatingVideoPlayer ───────────────────────────────────────────────────

export function FloatingVideoPlayer({ media, onClose }) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );
  const [isExpanded, setIsExpanded]   = useState(false);
  const [isDragging, setIsDragging]   = useState(false);
  const dragControls                  = useDragControls();
  const playerRef                     = useRef(null);

  // Écoute resize
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const w = isExpanded ? DESKTOP_W_MAX : DESKTOP_W;
  const h = isExpanded ? DESKTOP_H_MAX : DESKTOP_H;

  // ── Desktop : overlay flottant draggable ──────────────────────────────
  if (isDesktop) {
    return (
      <motion.div
        ref={playerRef}
        drag
        dragControls={dragControls}
        dragMomentum={false}
        dragElastic={0}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
        initial={{ opacity: 0, scale: 0.85, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.85, y: 20 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        style={{ width: w, zIndex: 200 }}
        className="fixed bottom-6 right-6 rounded-2xl overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.7)] border border-white/10 bg-black/90 backdrop-blur-xl"
      >
        {/* ── Barre de contrôle (drag handle) ── */}
        <div
          onPointerDown={(e) => dragControls.start(e)}
          className={`flex items-center justify-between px-4 py-2.5 bg-white/[0.03] border-b border-white/5 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <GripHorizontal size={12} className="text-white/20 shrink-0" />
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse shrink-0" />
            <span className="text-[9px] font-mono text-cyan-500/70 uppercase tracking-widest truncate">
              {media.title || 'Média_Actif'}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setIsExpanded(v => !v)}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-white/30 hover:text-white hover:bg-white/10 transition-all"
              aria-label={isExpanded ? 'Réduire' : 'Agrandir'}
            >
              {isExpanded
                ? <Minimize2 size={11} />
                : <Maximize2 size={11} />
              }
            </button>
            <button
              onClick={onClose}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-white/30 hover:text-white hover:bg-red-500/20 hover:text-red-400 transition-all"
              aria-label="Fermer"
            >
              <X size={11} />
            </button>
          </div>
        </div>

        {/* ── Zone vidéo ── */}
        <motion.div
          animate={{ height: h }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative bg-black overflow-hidden"
        >
          <MediaContent media={media} />
        </motion.div>

        {/* ── Pied de page (scanlines déco) ── */}
        <div className="px-4 py-1.5 flex items-center justify-between border-t border-white/5 bg-black/60">
          <span className="text-[8px] font-mono text-white/10 uppercase tracking-widest">
            PESA_MEDIA_PROTOCOL
          </span>
          <span className="text-[8px] font-mono text-cyan-900 uppercase">
            {isYoutube(media?.src) ? 'YT_EMBED' : media?.type?.toUpperCase()}
          </span>
        </div>

        {/* Scanlines overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,1) 2px, rgba(0,0,0,1) 4px)',
            zIndex: 10,
          }}
        />
      </motion.div>
    );
  }

  // ── Mobile : drawer depuis le bas ──────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[190]"
        onClick={onClose}
      />

      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 35 }}
        drag="y"
        dragConstraints={{ top: 0 }}
        dragElastic={0.1}
        onDragEnd={(_, info) => { if (info.offset.y > 80) onClose(); }}
        className="fixed bottom-0 left-0 right-0 z-[200] rounded-t-3xl overflow-hidden bg-black/95 border-t border-white/10 shadow-[0_-32px_80px_rgba(0,0,0,0.8)]"
        style={{ height: '75vh', maxHeight: '600px' }}
      >
        {/* Pill handle */}
        <div className="flex flex-col items-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header mobile */}
        <div className="flex items-center justify-between px-5 pb-3 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
            <span className="text-[9px] font-mono text-cyan-500/70 uppercase tracking-widest truncate max-w-[200px]">
              {media.title || 'Média'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-xl flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all"
          >
            <X size={14} />
          </button>
        </div>

        {/* Zone vidéo mobile */}
        <div className="flex-1 h-[calc(100%-80px)] bg-black">
          <MediaContent media={media} />
        </div>
      </motion.div>
    </>
  );
}

// ─── MediaContent — dispatch selon le type ────────────────────────────────

function MediaContent({ media }) {
  if (!media?.src) return null;

  if (media.type === 'pdf') {
    return (
      <div className="w-full h-full bg-slate-900">
        <iframe
          src={`${media.src}#view=FitH&toolbar=0`}
          className="w-full h-full border-none grayscale invert contrast-125 opacity-90"
          title={media.title}
        />
      </div>
    );
  }

  if (isYoutube(media.src) || media.type === 'youtube') {
    return (
      <YoutubeLazyEmbed
        src={media.src}
        title={media.title}
        autoplay={false}
      />
    );
  }

  // Vidéo native
  return (
    <video
      controls
      autoPlay
      src={media.src}
      className="w-full h-full object-contain bg-black"
      aria-label={media.title}
    />
  );
}